import type { FC } from 'react';

import { ReactComponent as Pix } from '../../assets/pix-mark.svg';
import { ReactComponent as Gmail } from './gmail.svg';
import { ReactComponent as Whatsapp } from './whatsapp.svg';
import styles from './LogosDeTerceiros.module.scss';

// LOGOTIPOS DE TERCEIROS — os desenhos oficiais das marcas, que não podem ser recoloridos nem
// redesenhados (por isso moram aqui, a única casa de cor literal da UI nova). Servem de
// `renderIcon` para o `Button` do Carbon: recebem `size` como os ícones dele.
//
// ⚠️ O Carbon repinta todo traço de ícone de botão com a cor do botão, menos os marcados com
// `data-icon-path` (é o mecanismo dele para ícones de duas cores). Os traços daqui levam a marca, e
// o G do Google fica nas quatro cores; a maçã da Apple pinta com o texto do tema (preta no claro,
// branca no escuro), como a Apple exige.

type PropsDoLogo = { size?: number | string; className?: string };

/** O "G" do Google, nas quatro cores da marca. */
export const LogoGoogle: FC<PropsDoLogo> = ({ size = 16, className }) => (
  <svg width={size} height={size} viewBox='0 0 48 48' className={className} aria-hidden focusable='false'>
    <path data-icon-path='marca' fill='#FFC107' d='M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z' />
    <path data-icon-path='marca' fill='#FF3D00' d='m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z' />
    <path data-icon-path='marca' fill='#4CAF50' d='M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z' />
    <path data-icon-path='marca' fill='#1976D2' d='M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z' />
  </svg>
);

/** A maçã da Apple: monocromática por regra da própria Apple. */
export const LogoApple: FC<PropsDoLogo> = ({ size = 16, className }) => (
  <svg width={size} height={size} viewBox='0 0 24 24' className={[styles.maca, className].filter(Boolean).join(' ')} aria-hidden focusable='false'>
    <path data-icon-path='marca' d='M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701' />
  </svg>
);

/** O envelope do Gmail, nas cores da marca. */
export const LogoGmail: FC<PropsDoLogo> = ({ size = 24, className }) => (
  <Gmail width={size} height={size} className={className} aria-hidden focusable='false' />
);

/** O símbolo do PIX, o MESMO vetor do app (`copiaDasBandeiras.test.ts`). Monocromático (a versão
 *  de uma cor que o manual da marca prevê): pinta com o ícone do tema, preto no claro e branco no
 *  escuro, como o cartão ao lado. */
export const LogoPix: FC<PropsDoLogo> = ({ size = 24, className }) => (
  <Pix width={size} height={size} className={[styles.pix, className].filter(Boolean).join(' ')} aria-hidden focusable='false' />
);

/** O balão do WhatsApp, nas cores da marca. */
export const LogoWhatsapp: FC<PropsDoLogo> = ({ size = 24, className }) => (
  <Whatsapp width={size} height={size} className={className} aria-hidden focusable='false' />
);
